import React, { createRef, HTMLAttributes } from "react"; /** React library **/
import ReactDOM from "react-dom";
/** Mobx library **/
import { inject, observer } from "mobx-react";
/** UI Component library */
import Scrollbar from "react-scrollbars-custom";
/** Type library */
import PropTypes from "prop-types";
/** Utility library */
import _ from "lodash";
import classNames from "classnames";
import DCMViewContainer from "pages/viewer-dashboard/container/viewLayout/DCMViewContainer";
import "./GridViewportSection.styl";

const TOP_CLASS = "top";
const BOTTOM_CLASS = "bottom";
const MIDDLE_CLASS = "middle";

interface layoutRowsCols {
  rows: number;
  cols: number;
}

/**
 * @description GridLayout UI Component Properties
 *
 * @interface IGridLayoutProps
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface IGridLayoutProps extends HTMLAttributes<HTMLDivElement> {
  synchronizationStore: any;
  seriesListStore: any;
  gridLayoutStore: any;
  measureToolStore: any;
  /**
   * @description 기본 그리드 레이아웃 행 값
   */
  rows: number;
  /**
   * @description 기본 그리드 레이아웃 열 값
   */
  cols: number;
  /**
   * @description 그리드 레이아웃 최대 행 값
   */
  maxRows: number;
  /**
   * @description 그리드 레이아웃 최대 열 값
   */
  maxCols: number;
}

/**
 * @description GridLayout UI Component Properties
 *
 * @interface IGridLayoutStates
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface IGridLayoutStates extends HTMLAttributes<HTMLDivElement> {
  /**
   * @description 현재 확장 UI 열림/닫힘 상태
   */
}

@inject("seriesListStore")
@inject("gridLayoutStore")
@inject("measureToolStore")
@inject("synchronizationStore")
@observer
class GridLayout extends React.Component<IGridLayoutProps, IGridLayoutStates> {
  static propTypes: PropTypes.InferProps<IGridLayoutProps>;

  static defaultProps = {
    rows: 1,
    cols: 1,
    maxRows: 4,
    maxCols: 4,
  };

  element: any = createRef();

  constructor(props: any) {
    super(props);
  }

  componentDidMount = () => {
    const { synchronizationStore } = this.props;
    //store.SelectedMeasure.setSelectedMeasure("");
    synchronizationStore.registerContainerElement(this.element);
  };

  componentWillUnmount = () => {
    const { synchronizationStore } = this.props;
    synchronizationStore.unregisterContainerElement(this.element);
  };

  getLayoutRowsCols(): layoutRowsCols {
    const { gridLayoutStore } = this.props;
    const layout: layoutRowsCols = {
      rows: gridLayoutStore.getLayoutRows(),
      cols: gridLayoutStore.getLayoutColumns(),
    };

    return layout;
  }
  /**
   * @method getViewports
   * @description 모든 viewport 설정 반환
   * @return 모든 viewport 설정
   * @memberof GridLayout
   */

  getViewports() {
    const { gridLayoutStore } = this.props;
    // HACK : 추후, SplitSeries 에서 레이아웃이 자유로워지면 아래 구문이 맞을것
    let maxRows = gridLayoutStore.getLayoutMaxRows();
    let maxColumns = gridLayoutStore.getLayoutMaxColumns();
    let totalPages = gridLayoutStore.getLayoutTotalPages();

    // HACK : 현재 SplitSeries 는 3x4 만 지원하기때문에, 아래 구문 필요
    if (totalPages > 1) {
      maxRows = 2;
      maxColumns = 3;
    }

    //const numOfDCMView = maxRows * maxColumns;
    const numOfDCMView = maxRows * maxColumns * (totalPages + 1);
    var viewports = [];
    for (let i = 0; i < numOfDCMView; i++) {
      const viewportIndex = gridLayoutStore.getDCMViewOrder(i);
      viewports.push({
        viewportIndex: viewportIndex,
        DCMViewIdx: i,
        order: i,
        maxRows,
        maxColumns,
      });
    }

    return viewports;
  }

  /**
   * @method getViewportHeight
   * @description 현재 레이아웃 설정에서 viewport 높이 계산해서 반환
   * @returns 현재 레이아웃 설정에서 viewport 높이 계산
   * @memberof GridLayout
   */
  getViewportHeight() {
    const { gridLayoutStore } = this.props;
    const rows = gridLayoutStore.getLayoutRows();
    return 100 / rows;
  }

  /**
   * @method getViewportWidth
   * @description 현재 레이아웃 설정에서 viewport 너비 계산해서 반환
   * @returns 현재 레이아웃 설정에서 viewport 너비 계산
   * @memberof GridLayout
   */

  getViewportWidth() {
    const { gridLayoutStore } = this.props;
    const columns = gridLayoutStore.getLayoutColumns();
    return 100 / columns;
  }

  /**
   * @method getViewportPositionClass
   * @description i번째 viewport의 위치 정보를 표시하는 클래스 리턴
   * @returns i번째 viewport의 위치 정보를 표시하는 클래스 리턴
   * @todo (추후 CSS에 활용..?)
   * @memberof GridLayout
   */

  getViewportPositionClass(index: any) {
    const { gridLayoutStore } = this.props;

    const rows = gridLayoutStore.getLayoutRows();
    const columns = gridLayoutStore.getLayoutColumns();

    if (rows === 1) {
      return `${TOP_CLASS} ${BOTTOM_CLASS}`;
    }

    const actualRow = Math.floor(index / columns);

    if (actualRow === 0) {
      return TOP_CLASS;
    }
    if (actualRow + 1 === rows) {
      return BOTTOM_CLASS;
    }

    return MIDDLE_CLASS;
  }

  /**
   * @method getLayoutClass
   * @description 현재 레이아웃 설정 클래스를 리턴
   * @returns 현재 레이아웃 설정 클래스를 리턴
   * @memberof GridLayout
   */
  getLayoutClass() {
    const { gridLayoutStore } = this.props;

    const rows = gridLayoutStore.getLayoutRows();
    const columns = gridLayoutStore.getLayoutColumns();
    const layoutClass = `layout-${rows}-${columns}`;

    return layoutClass;
  }

  getVisibility(isVisible: Boolean) {
    if (isVisible === false) {
      return "hidden-dcmview";
    }
    return "";
  }

  getFocusClass() {
    const { seriesListStore } = this.props;
    const isSeriesOpen = seriesListStore.getIsOpen();

    if (isSeriesOpen === true) {
      return "focus-dcmview--series-open";
    } else {
      return "focus-dcmview--series-close";
    }
  }

  /**
   * @method renderViewport
   * @description i 번 Viewport를 렌더링
   * @param {*} i
   * @returns 렌더링된 viewport의 react element
   * @memberof GridLayout
   */
  renderViewport(orderIdx: number) {
    // Lazy-load 개발을 위한 임시 작업 코드
    // const pageIdx = orderIdx / layoutColumns / layoutRows;
    // const height = this.getViewportHeight();
    // //const displayTop = 280 * pageIdx;
    // const displayTop = 0;
    return (
      <DCMViewContainer
        key={orderIdx}
        number={orderIdx}
        frameRange={{ min: -1, max: -1 }}
        width={this.getViewportWidth()}
        height={this.getViewportHeight()}
      />
    );

    // return (
    //   <LazyLoad
    //     key={orderIdx}
    //     //once
    //     debounce={true}
    //     unmountIfInvisible={true}
    //     //height={[0 280}
    //     //placeholder={<PlaceHolder />}
    //     scroll={true}
    //     offset={[0, displayTop]}
    //     scrollContainer={
    //       "#layoutManagerTarget > div > div.ScrollbarsCustom-Wrapper > div"
    //     }
    //   >
    //     <DCMViewContainer
    //       key={orderIdx}
    //       number={DCMViewIdx}
    //       width={this.getViewportWidth()}
    //       height={this.getViewportHeight()}
    //     />
    //   </LazyLoad>
    // );
  }

  /**
   * @method render
   * @description GridViewportSection UI 컴포넌트 렌더링
   * @returns
   * @memberof GridLayout
   */
  render() {
    const { gridLayoutStore } = this.props;

    const rows = gridLayoutStore.getLayoutRows();
    const columns = gridLayoutStore.getLayoutColumns();
    const width = this.getViewportWidth();
    const height = this.getViewportHeight();

    // 제약 사항 : 최대 32개 화면까지(SplitSeries 기능 제한)
    //const totalPages = Math.min(gridLayoutStore.getLayoutTotalPages(), 3);
    const totalPages = Math.max(gridLayoutStore.getLayoutTotalPages(), 1);

    //const numOfDCMView = (rows + 1) * (columns + 1) * totalPages;
    const focusDCMViewIdx = gridLayoutStore.getFocusOneDCMViewIdx();
    const numOfDCMView = rows * columns * totalPages;

    return (
      <div id="layoutManagerTarget" className={this.getLayoutClass()}>
        <Scrollbar
          download=""
          inlist=""
          noScrollX={true}
          noScrollY={totalPages <= 1 ? true : false}
          noDefaultStyles
          permanentTrackY={false}
          onContextMenu={(event) => event.preventDefault()}
        >
          <div
            id="imageViewerViewports"
            ref={(input) => {
              this.element = input;
            }}
          >
            {this.getViewports().map((viewport, idx) => {
              let isVisible = true;
              // XXX : 불필요한 렌더링 발생
              // SplitSeries On/Off 차이 문제로 인해 임시 구문 추가
              if (viewport.order >= numOfDCMView && totalPages === 1) {
                isVisible = false;
              }
              //---
              let isFocus = false;
              if (viewport.viewportIndex === focusDCMViewIdx) {
                isFocus = true;
              }
              //---
              // forEach doesn't work
              if (isVisible) {
                return (
                  <div
                    key={viewport.viewportIndex}
                    //key={"viewport-" + viewport.viewportIndex}
                    className={classNames(
                      "viewportContainer " +
                        this.getViewportPositionClass(viewport.viewportIndex),
                      this.getVisibility(isVisible),
                      isFocus === false ? "" : this.getFocusClass()
                    )}
                    id="viewportContainer"
                    // style={{
                    //   height: this.getViewportHeight() + "%",
                    //   width: this.getViewportWidth() + "%",
                    // }}
                    style={{
                      height: isFocus ? "100%" : this.getViewportHeight() + "%",
                      width: isFocus ? "100%" : this.getViewportWidth() + "%",
                    }}
                  >
                    {/* {this.renderViewport(viewport.viewportIndex)} */}
                    {this.renderViewport(viewport.viewportIndex)}
                  </div>
                );
              }
            })}
          </div>
        </Scrollbar>
      </div>
    );
  }
}

export default GridLayout;
